import {
  type AddressConstructorData,
  Address,
} from '@webapp/core/entities/internal/Address'
import { type LegalEntitySummary } from '@webapp/core/entities/internal/LegalEntitySummary'

export interface PrimaryCategoryConfigDetailConstructor {
  id: number
  name: string
  email: Nullable<string>
  phoneOne: string
  fax: Nullable<string>
  url: Nullable<string>
  externalId: Nullable<string>
  active: boolean
  headquarter: boolean
  editable: boolean
  logo: Nullable<string>
  locationTimezone: string
  language: number
  currency: number
  primaryBillingAddress: AddressConstructorData
  primaryShippingAddress: AddressConstructorData
  shippingAddresses: AddressConstructorData[]
  legalEntity?: Nullable<LegalEntitySummary>
}

export class PrimaryCategoryConfigDetail {
  readonly id: number
  readonly name: string
  readonly email: Nullable<string>
  readonly phoneOne: string
  readonly fax: Nullable<string>
  readonly url: Nullable<string>
  readonly externalId: Nullable<string>
  readonly active: boolean
  readonly headquarter: boolean
  readonly editable: boolean
  readonly logo: Nullable<string>
  readonly locationTimezone: string
  readonly language: number
  readonly currency: number
  readonly primaryBillingAddress: Address
  readonly primaryShippingAddress: Address
  readonly shippingAddresses: Address[]
  readonly legalEntity: Nullable<LegalEntitySummary>

  constructor(data: PrimaryCategoryConfigDetailConstructor) {
    this.id = data.id
    this.name = data.name
    this.email = data.email
    this.phoneOne = data.phoneOne
    this.fax = data.fax
    this.url = data.url
    this.externalId = data.externalId
    this.active = data.active
    this.headquarter = data.headquarter
    this.editable = data.editable
    this.logo = data.logo
    this.locationTimezone = data.locationTimezone
    this.language = data.language
    this.currency = data.currency
    this.primaryBillingAddress = new Address(data.primaryBillingAddress)
    this.primaryShippingAddress = new Address(data.primaryShippingAddress)
    this.shippingAddresses = data.shippingAddresses.map(
      (addr) => new Address(addr)
    )
    this.legalEntity = data.legalEntity ?? null
  }

  get hasLogo(): boolean {
    return !!this.logo
  }

  get isHeadquarters(): boolean {
    return this.headquarter
  }

  get canEdit(): boolean {
    return this.editable
  }

  get hasAdditionalShippingAddresses(): boolean {
    return this.shippingAddresses.length > 0
  }

  get totalShippingAddresses(): number {
    return 1 + this.shippingAddresses.length
  }

  get hasContactEmail(): boolean {
    return !!this.email
  }

  get hasFax(): boolean {
    return !!this.fax
  }

  get hasWebsite(): boolean {
    return !!this.url
  }

  get hasExternalId(): boolean {
    return !!this.externalId
  }

  get canActivate(): boolean {
    return !this.active
  }

  get canDeactivate(): boolean {
    return this.active && !this.headquarter && this.editable
  }

  get canSetAsHeadquarters(): boolean {
    return this.active && !this.headquarter && this.editable
  }
}
